import React, { useState, useEffect, useRef } from 'react';
import MapContainer from './mapComponents/MapContainer';
import { usePinToBoard } from '../contexts/PinToBoardContext';

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const IntelligenceTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'updated_date', direction: 'desc' });
  const [filters, setFilters] = useState({ provider: '', location: '' });
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, pages: 0 });
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const isMountedRef = useRef(true);
  const { pinItem } = usePinToBoard();

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = async (signal) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        sort: sortConfig.key,
        order: sortConfig.direction,
        limit: pagination.limit,
        offset: pagination.offset
      });

      if (filters.provider) params.append('provider', filters.provider);
      if (filters.location) params.append('location', filters.location);

      const response = await fetch(`${API_BASE_URL}/api/data/intelligence?${params}`, {
        credentials: 'include',
        signal
      });

      const result = await response.json();

      if (!isMountedRef.current) return;

      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
        setError(null);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (!isMountedRef.current) return;
      setError('Network error. Please try again.');
      console.error('Error fetching intelligence data:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [sortConfig, filters, pagination.offset, pagination.limit]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 })); // Reset to first page
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  const ensureProtocol = (url) => {
    if (!url) return null;
    //console.log(url)
    // If URL already has protocol, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Add https:// to URLs without protocol
    return `https://${url}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      setDeleteLoading(id);
      setDeleteError(null);
      setDeleteSuccess(null);

      const response = await fetch(`${API_BASE_URL}/api/data/intelligence/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setDeleteSuccess(`"${result.data.title}" deleted successfully`);
        // Refresh data
        fetchData();
      } else {
        setDeleteError(result.message || 'Failed to delete record');
      }
    } catch (err) {
      setDeleteError('Network error. Please try again.');
      console.error('Error deleting intelligence record:', err);
    } finally {
      setDeleteLoading(null);
    }
  };


  if (loading && data.length === 0) {
    return (
      <div className="table-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading intelligence data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>Market Intelligence Data</h2>
        <p className="table-subtitle">
          Real estate listings and market data collected from various sources
        </p>
      </div>

      {/* Filters */}
      <div className="table-filters">
        <div className="filter-group">
          <label>Provider:</label>
          <input
            type="text"
            placeholder="Filter by provider..."
            value={filters.provider}
            onChange={(e) => handleFilterChange('provider', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Location:</label>
          <input
            type="text"
            placeholder="Filter by location..."
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="intel-items-per-page">Items per page:</label>
          <select
            id="intel-items-per-page"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="items-per-page-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <button 
          className="filter-clear"
          onClick={() => {
            setFilters({ provider: '', location: '' });
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Map Component */}
      {data.length > 0 && !loading && (
        <div style={{ height: '400px', width: '100%', marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <MapContainer entries={data} />
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      )}
      
      {deleteError && (
        <div className="error-message">
          <p>⚠️ {deleteError}</p>
          <button onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}
      
      {deleteSuccess && (
        <div className="success-message">
          <p>✅ {deleteSuccess}</p>
          <button onClick={() => setDeleteSuccess(null)}>Dismiss</button>
        </div>
      )}

      {data.length === 0 && !loading ? (
        <div className="no-data">
          <p>📊 No intelligence data found</p>
          <p>Data will appear here once the bot collects information based on your requests.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('title')} className="sortable">
                    Title {getSortIcon('title')}
                  </th>
                  <th onClick={() => handleSort('type')} className="sortable">
                    Type {getSortIcon('type')}
                  </th>
                  <th onClick={() => handleSort('provider')} className="sortable">
                    Provider {getSortIcon('provider')}
                  </th>
                  <th onClick={() => handleSort('keyword')} className="sortable">
                    Keyword {getSortIcon('keyword')}
                  </th>
                  <th onClick={() => handleSort('price')} className="sortable">
                    Price {getSortIcon('price')}
                  </th>
                  <th onClick={() => handleSort('location')} className="sortable">
                    Location {getSortIcon('location')}
                  </th>
                  <th onClick={() => handleSort('area')} className="sortable">
                    Area {getSortIcon('area')}
                  </th>
                  <th onClick={() => handleSort('updated_date')} className="sortable">
                    Date {getSortIcon('updated_date')}
                  </th>
                  <th>Description</th>
                  <th>Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id}>
                    <td className="title-cell">
                      <strong>{truncateText(item.title, 60)}</strong>
                    </td>
                    <td>
                      <span className={`type-badge ${item.type?.toLowerCase()}`}>
                        {item.type || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className="provider-tag">{item.provider || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="keyword-badge">{item.keyword || 'N/A'}</span>
                    </td>
                    <td className="price-cell">
                      <strong>{item.price || 'N/A'}</strong>
                    </td>
                    <td>{truncateText(item.location, 30)}</td>
                    <td>{item.area || 'N/A'}</td>
                    <td>{formatDate(item.updated_date)}</td>
                    <td className="description-cell">
                      {truncateText(item.description, 80)}
                    </td>
                    <td>
                      {item.url ? (
                        <a 
                          href={ensureProtocol(item.url)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="url-link"
                        >
                          View 🔗
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="pin-btn"
                        title="Pin to Board"
                        onClick={(e) =>
                          pinItem(
                            {
                              item_type: 'intelligence',
                              ref_id: String(item.id),
                              title: item.title || item.location || 'Intelligence item',
                              metadata: {
                                price: item.price,
                                location: item.location,
                                provider: item.provider,
                                type: item.type,
                                url: item.url,
                              },
                              tags: [item.keyword].filter(Boolean),
                            },
                            e.currentTarget,
                          )
                        }
                      >
                        <i className="fas fa-thumbtack" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id, item.title)}
                        disabled={deleteLoading === item.id}
                        className="delete-btn"
                        title="Delete record"
                      >
                        {deleteLoading === item.id ? '⏳' : '🗑️'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-cards">
            {data.map((item) => (
              <div key={item.id} className="mobile-card">
                <div className="mobile-card-header">
                  <h3 className="mobile-card-title">
                    {truncateText(item.title, 80)}
                  </h3>
                  <div className="mobile-card-badges">
                    <span className={`type-badge ${item.type?.toLowerCase()}`}>
                      {item.type || 'N/A'}
                    </span>
                    <span className="keyword-badge">{item.keyword || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="mobile-card-content">
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Price</span>
                    <span className="mobile-card-value price">
                      {item.price || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Area</span>
                    <span className="mobile-card-value">
                      {item.area || 'N/A'}
                    </span>
                  </div>

                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Date</span>
                    <span className="mobile-card-value">
                      {formatDate(item.updated_date)}
                    </span>
                  </div>
                  
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Provider</span>
                    <span className="mobile-card-value">
                      <span className="provider-tag">{item.provider || 'N/A'}</span>
                    </span>
                  </div>
                  
                  <div className="mobile-card-field">
                    <span className="mobile-card-label">Location</span>
                    <span className="mobile-card-value">
                      {item.location || 'N/A'}
                    </span>
                  </div>
                  
                  {item.description && (
                    <div className="mobile-card-field full-width">
                      <span className="mobile-card-label">Description</span>
                      <span className="mobile-card-value">
                        {truncateText(item.description, 120)}
                      </span>
                    </div>
                  )}
                </div>
                
                {item.url && (
                  <div className="mobile-card-actions">
                    <a 
                      href={ensureProtocol(item.url)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="url-link"
                    >
                      View Details 🔗
                    </a>
                    <button
                      onClick={() => handleDelete(item.id, item.title)}
                      disabled={deleteLoading === item.id}
                      className="delete-btn mobile-delete-btn"
                      title="Delete record"
                    >
                      {deleteLoading === item.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                )}
                {!item.url && (
                  <div className="mobile-card-actions">
                    <button
                      onClick={() => handleDelete(item.id, item.title)}
                      disabled={deleteLoading === item.id}
                      className="delete-btn mobile-delete-btn"
                      title="Delete record"
                    >
                      {deleteLoading === item.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} records
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={pagination.offset === 0}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange((pagination.pages - 1) * pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {loading && data.length > 0 && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default IntelligenceTable;
